import { type VariantProps, cva } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '../../../lib';
import { type Tone, lightToneClasses } from '../tone';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        gold: 'border-transparent bg-[#d4af37] text-black hover:bg-[#d4af37]/90',
        light: 'border-transparent font-medium',
      },
      tone: {
        primary: '',
        muted: '',
        info: '',
        success: '',
        warning: '',
        destructive: '',
      } satisfies Record<Tone, string>,
      size: {
        sm: 'px-2 py-0.5',
        md: 'px-2.5 py-0.5',
      },
    },
    compoundVariants: (Object.keys(lightToneClasses) as Tone[]).map(tone => ({
      variant: 'light' as const,
      tone,
      className: lightToneClasses[tone],
    })),
    defaultVariants: {
      variant: 'default',
      tone: 'muted',
      size: 'md',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, tone, size, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant, tone, size }), className)} {...props} />;
}

export { Badge, badgeVariants };
